/**
 * Main Mermaid editor dialog
 * Combines visual editor, code editor, toolbar, and AI panel
 */

import { useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Code2, Copy, Download, Eye, Loader2, PanelLeft, Save, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { MermaidCodeEditor } from "./mermaid-code-editor"
import { MermaidToolbar } from "./mermaid-toolbar"
import { MermaidVisualEditor } from "./mermaid-visual-editor"
import { useMermaidEditorStore } from "../stores/mermaid-editor-store"
import { exportMermaidAsPng, exportMermaidAsSvg, renderSanitizedMermaidSvg } from "../utils"

type MermaidEditorDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialCode?: string
  title?: string
  onSave?: (code: string, title: string) => Promise<void>
  readOnly?: boolean
  showSaveButton?: boolean
  diagramId?: string | null
}

export function MermaidEditorDialog({
  open,
  onOpenChange,
  initialCode,
  title: initialTitle = "Untitled Diagram",
  onSave,
  readOnly = false,
  showSaveButton = true,
  diagramId = null,
}: MermaidEditorDialogProps) {
  const { t } = useTranslation()
  const { code, title, setTitle, isDirty, loadDiagram, markClean, resetDiagram } =
    useMermaidEditorStore()

  const [activeTab, setActiveTab] = useState<"visual" | "code">("visual")
  const [isSaving, setIsSaving] = useState(false)
  const [showToolbar, setShowToolbar] = useState(true)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState(initialTitle)
  const [hasInitialized, setHasInitialized] = useState(false)

  useEffect(() => {
    if (open && !hasInitialized) {
      if (initialCode) {
        loadDiagram(diagramId, initialTitle, initialCode)
      } else {
        resetDiagram()
        setTitle(initialTitle)
      }
      setTitleValue(initialTitle)
      setHasInitialized(true)
    } else if (!open && hasInitialized) {
      setHasInitialized(false)
    }
  }, [
    open,
    hasInitialized,
    initialCode,
    initialTitle,
    diagramId,
    loadDiagram,
    resetDiagram,
    setTitle,
  ])

  const handleSave = useCallback(async () => {
    if (!onSave || isSaving) return

    setIsSaving(true)
    try {
      await onSave(code, title)
      markClean()
      toast.success(t("mermaid.saved", "Diagram saved"))
    } catch {
      toast.error(t("mermaid.save_error", "Failed to save diagram"))
    } finally {
      setIsSaving(false)
    }
  }, [onSave, code, title, markClean, t, isSaving])

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(code)
    toast.success(t("mermaid.copied", "Code copied to clipboard"))
  }, [code, t])

  const handleExportSvg = useCallback(async () => {
    try {
      await exportMermaidAsSvg(code, title)
      toast.success(t("mermaid.exported_svg", "Diagram exported as SVG"))
    } catch {
      toast.error(t("mermaid.export_error", "Failed to export diagram"))
    }
  }, [code, title, t])

  const handleExportPng = useCallback(async () => {
    try {
      await exportMermaidAsPng(code, title)
      toast.success(t("mermaid.exported_png", "Diagram exported as PNG"))
    } catch {
      toast.error(t("mermaid.export_error", "Failed to export diagram"))
    }
  }, [code, title, t])

  const handleTitleSave = useCallback(() => {
    if (titleValue.trim()) {
      setTitle(titleValue.trim())
    }
    setIsEditingTitle(false)
  }, [titleValue, setTitle])

  const handleClose = useCallback(() => {
    if (isDirty) {
      const confirmed = window.confirm(
        t("mermaid.unsaved_changes", "You have unsaved changes. Are you sure you want to close?")
      )
      if (!confirmed) return
    }
    onOpenChange(false)
  }, [isDirty, onOpenChange, t])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="flex h-[90vh] max-h-[900px] w-[95vw] max-w-[1400px] flex-col gap-0 p-0 [&>button]:hidden"
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement
          const cursor = window.getComputedStyle(target).cursor

          // Prevent close if interacting with resize handles or ReactFlow controls
          if (cursor.includes("resize") || target.closest(".react-flow__controls")) {
            e.preventDefault()
          }
          // Otherwise allow close (backdrop clicks)
        }}
      >
        <DialogHeader className="flex-shrink-0 border-b px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {isEditingTitle ? (
                <Input
                  className="h-8 w-64"
                  value={titleValue}
                  onBlur={handleTitleSave}
                  onChange={(event) => setTitleValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleTitleSave()
                    if (event.key === "Escape") {
                      setTitleValue(title)
                      setIsEditingTitle(false)
                    }
                  }}
                />
              ) : (
                <DialogTitle
                  className="hover:text-primary cursor-pointer truncate"
                  onClick={() => !readOnly && setIsEditingTitle(true)}
                >
                  {title}
                  {isDirty && <span className="text-muted-foreground ml-2">*</span>}
                </DialogTitle>
              )}
            </div>

            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="size-9"
                    size="icon"
                    variant="ghost"
                    onClick={() => setShowToolbar(!showToolbar)}
                  >
                    <PanelLeft className={cn("size-4", !showToolbar && "text-muted-foreground")} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {showToolbar
                    ? t("mermaid.hide_toolbar", "Hide toolbar")
                    : t("mermaid.show_toolbar", "Show toolbar")}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button className="size-9" size="icon" variant="ghost" onClick={handleCopy}>
                    <Copy className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("mermaid.copy_code", "Copy code")}</TooltipContent>
              </Tooltip>
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button className="size-9" size="icon" variant="ghost">
                        <Download className="size-4" /> {/* eslint-disable-line */}
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>{t("mermaid.export", "Export")}</TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportSvg}>
                    {t("mermaid.export_svg", "Export as SVG")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportPng}>
                    {t("mermaid.export_png", "Export as PNG")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {showSaveButton && onSave && (
                <>
                  <div className="bg-border mx-1 h-6 w-px" />
                  <Button
                    disabled={!isDirty || isSaving || readOnly}
                    size="sm"
                    onClick={handleSave}
                  >
                    {isSaving ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 size-4" />
                    )}
                    {t("mermaid.save", "Save")}
                  </Button>
                </>
              )}

              <div className="bg-border mx-1 h-6 w-px" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button className="size-9" size="icon" variant="ghost" onClick={handleClose}>
                    <X className="size-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("common.close", "Close")}</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <DialogDescription className="sr-only">
            {t("mermaid.editor_description", "Edit your Mermaid diagram")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          {showToolbar && <MermaidToolbar readOnly={readOnly} />}

          <ResizablePanelGroup className="min-h-0 flex-1" direction="horizontal">
            <ResizablePanel defaultSize={60} minSize={30}>
              <Tabs
                className="flex h-full flex-col"
                value={activeTab}
                onValueChange={(value) => setActiveTab(value as typeof activeTab)}
              >
                <TabsList className="mx-3 mt-3 w-fit">
                  <TabsTrigger className="gap-2" value="visual">
                    <Eye className="size-4" />
                    {t("mermaid.visual", "Visual")}
                  </TabsTrigger>
                  <TabsTrigger className="gap-2" value="code">
                    <Code2 className="size-4" />
                    {t("mermaid.code", "Code")}
                  </TabsTrigger>
                </TabsList>

                <TabsContent className="m-0 min-h-0 flex-1 p-3" value="visual">
                  <MermaidVisualEditor className="h-full rounded-md border" readOnly={readOnly} />
                </TabsContent>

                <TabsContent className="m-0 min-h-0 flex-1" value="code">
                  <MermaidCodeEditor readOnly={readOnly} showPreview={false} />
                </TabsContent>
              </Tabs>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={40} minSize={20}>
              <div className="flex h-full flex-col">
                <div className="border-b px-4 py-3">
                  <h3 className="font-medium">{t("mermaid.preview", "Preview")}</h3>
                </div>
                <div className="min-h-0 flex-1 overflow-auto p-4">
                  <MermaidPreview code={code} />
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function MermaidPreview({ code }: { code: string }) {
  const { t } = useTranslation()
  const [svg, setSvg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isRendering, setIsRendering] = useState(false)

  useEffect(() => {
    if (!code.trim()) {
      setSvg(null)
      setError(null)
      return
    }

    const render = async () => {
      setIsRendering(true)
      try {
        const renderedSvg = await renderSanitizedMermaidSvg(`preview-${Date.now()}`, code)
        setSvg(renderedSvg)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to render")
        setSvg(null)
      } finally {
        setIsRendering(false)
      }
    }

    const timeout = setTimeout(render, 300)
    return () => clearTimeout(timeout)
  }, [code])

  if (isRendering) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground size-8 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-destructive/10 text-destructive rounded-md p-4 text-sm">
        <p className="font-medium">{t("mermaid.preview_error", "Preview error")}</p>
        <pre className="mt-2 text-xs whitespace-pre-wrap opacity-75">{error}</pre>
      </div>
    )
  }

  if (!svg) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
        {t("mermaid.no_preview", "Enter Mermaid code to see preview")}
      </div>
    )
  }

  return (
    <div
      className="flex justify-center [&_svg]:max-w-full"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}
